import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button, Input, Label } from "@f2e/ui/components/base"

interface Option {
  label: string
  value: string
}

export interface OptionsEditorProps<T extends { options?: Option[] }> {
  field: T
  onChange: (next: T) => void
  disabled?: boolean
  title?: string
}

export const OptionsEditor = <T extends { options?: Option[] }>({
  field,
  onChange,
  disabled = false,
  title,
}: OptionsEditorProps<T>) => {
  const { t } = useTranslation()
  const options = field.options || []
  const slugify = (value: string): string => {
    return (
      value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "option"
    )
  }
  return (
    <div className="space-y-2">
      <Label className="text-xs">
        {title ??
          t("workspace.agent.basicForm.formBuilder.optionsEditor.title")}
      </Label>
      <div className="space-y-2">
        {options.map((opt, i) => (
          <div className="grid grid-cols-[1fr_auto] gap-2" key={String(i)}>
            <Input
              className=""
              value={opt.label}
              onChange={(e) => {
                const newLabel = e.target.value
                const next = {
                  ...field,
                  options: [
                    ...options.slice(0, i),
                    {
                      ...opt,
                      label: newLabel,
                      value: `${slugify(newLabel)}_${i}`,
                    },
                    ...options.slice(i + 1),
                  ],
                }
                onChange(next)
              }}
              disabled={disabled}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => {
                const next = {
                  ...field,
                  options: options.filter((_, idx) => idx !== i),
                }
                onChange(next)
              }}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          const next = {
            ...field,
            options: [
              ...options,
              { label: "", value: `new_option_${options.length + 1}` },
            ],
          }
          onChange(next)
        }}
        disabled={disabled}
      >
        {t("workspace.agent.basicForm.formBuilder.optionsEditor.addOption")}
      </Button>
    </div>
  )
}

export default OptionsEditor
